import {Component, ElementRef, HostListener, inject, OnInit, signal, DOCUMENT} from '@angular/core';
import {
  NotificationPanelToggleComponent
} from './components/notification-panel-toggle/notification-panel-toggle.component';
import {NotificationPanelComponent} from './components/notification-panel/notification-panel.component';
import {NotificationService} from './notification.service';
import {NgComponentOutlet} from '@angular/common';
import {NOTIFICATION_CLICK_HANDLER_TOKEN} from './components/notification-click.handler';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {tap} from 'rxjs';

@Component({
  selector: 'app-notifications',
  imports: [
    NotificationPanelToggleComponent,
    NotificationPanelComponent,
    NgComponentOutlet,
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit {
  // Inject the notification service
  private readonly notificationService = inject(NotificationService);
  private readonly clickHandler = inject(NOTIFICATION_CLICK_HANDLER_TOKEN);

  readonly notificationClickHandler$ = this.clickHandler.listener().pipe(
    tap(this.onNotificationClick.bind(this)),
    takeUntilDestroyed(),
  );

  // Get notifications from the service
  readonly notifications = this.notificationService.notifications;

  // Signal to track panel open state
  private readonly panelOpen = signal(false);

  // Reference to the component's host element
  private readonly elementRef = inject(ElementRef);

  // Document reference for DOM operations
  private readonly document = inject(DOCUMENT);

  // Method to check if panel is open
  isPanelOpen() {
    return this.panelOpen();
  }

  // Method to toggle panel open/closed
  togglePanel(isOpen: boolean) {
    this.panelOpen.set(isOpen);
  }

  // Method to handle notification item clicks
  onNotificationClick(notificationId: string) {
    console.log(`Notification clicked: ${notificationId}`);
    this.togglePanel(false);
    this.notificationService.readNotification(notificationId);
    // Additional handling can be added here
  }

  // Method to clear all notifications
  onClearAllNotifications() {
    this.notificationService.resetNotifications();
    // Close the panel after clearing
    this.togglePanel(false);
  }

  // Listen for document click events
  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    // If panel is not open, no need to do anything
    if (!this.isPanelOpen()) {
      return;
    }

    // Check if the click was outside the component
    const clickedInside = this.elementRef.nativeElement.contains(event.target as Node);
    if (!clickedInside) {
      // Close the panel
      this.togglePanel(false);
    }
  }

  // Listen for keydown events to close panel on ESC key
  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    // If panel is not open, no need to do anything
    if (!this.isPanelOpen()) {
      return;
    }

    // Check if ESC key was pressed
    if (event.key === 'Escape') {
      // Close the panel
      this.togglePanel(false);
    }
  }

  ngOnInit(): void {
    this.notificationClickHandler$.subscribe();
  }
}
